import { push } from 'connected-react-router'
import { takeLatest, put, all, call, select } from 'redux-saga/effects'

import fetchClient from '~/services/fetchClient'
import newrelic from '~/services/newrelic'

import { resolvePrizeIdentification, resolvePrizeRegistration } from './actions'
import { getGenderApiValue } from '~/pages/premiacao/utils/gender'
import { PRIZE_ERROR_CODES } from '~/helpers/constants'

const GENERIC_ERROR_MESSAGE = 'Ocorreu um erro ao processar a requisição.'

const isValidationError = (payload) => payload?.code === PRIZE_ERROR_CODES.VALIDATION_ERROR

const buildRegisterPayload = ({
  consumerCpf,
  consumerName,
  consumerEmail,
  consumerBirthday,
  consumerCellphone,
  consumerGenderIdentity,
  consumerLoyaltyOptIn,
  consumerRegulationAccepted
}) => {
  const normalizedBirthday = consumerBirthday?.includes('/')
    ? consumerBirthday.split('/').reverse().join('-')
    : consumerBirthday

  return {
    consumerCpf,
    consumerName,
    consumerEmail,
    consumerBirthday: normalizedBirthday,
    consumerCellphone,
    genderIdentity: getGenderApiValue(consumerGenderIdentity),
    acceptTermsOfUse: consumerRegulationAccepted,
    loyaltyOptIn: consumerLoyaltyOptIn
  }
}

const isDeadlineExpired = (errorCode) => errorCode === PRIZE_ERROR_CODES.PICKUP_DEADLINE_EXPIRED
const consumerHasVoucher = (consumer) => Boolean(consumer?.voucher)

export function* identifyConsumer({ payload }) {
  let response
  const { campaignId, consumerCpf } = payload

  try {
    if (!campaignId || !consumerCpf) {
      throw new Error(GENERIC_ERROR_MESSAGE)
    }

    const url = `/campaign/${campaignId}/prize/identify`

    response = yield call(fetchClient.post, url, { consumerCpf })

    const consumer = response.data?.data?.[0]

    if (!response.data?.success || !consumer) {
      const errorCode = response.data?.code

      if (isDeadlineExpired(errorCode)) {
        yield put(resolvePrizeIdentification({ error: { message: response.data?.message, code: errorCode } }))
        yield put(push(`/${campaignId}/PARTICIPACAO_GARANTIDA`))
        return
      }

      const businessError = new Error(response.data?.message || GENERIC_ERROR_MESSAGE)
      businessError.code = errorCode

      throw businessError
    }

    yield put(
      resolvePrizeIdentification({
        consumer: {
          ...consumer,
          consumerCpf
        }
      })
    )

    if (consumerHasVoucher(consumer)) {
      yield put(push(`/${campaignId}/CADASTRO_CONCLUIDO`))
    } else {
      yield put(push(`/${campaignId}/CADASTRO`))
    }
  } catch (error) {
    const apiError = error?.response?.data

    newrelic.noticeError(error, { response: apiError, payload })
    yield put(push(`/${campaignId}/SISTEMA_INDISPONIVEL`))
  }
}

export function* registerConsumer({ payload }) {
  let response
  const { campaignId, consumer } = payload

  try {
    if (!campaignId || !consumer) {
      throw new Error(GENERIC_ERROR_MESSAGE)
    }

    const url = `/campaign/${campaignId}/prize/register`

    response = yield call(fetchClient.post, url, buildRegisterPayload(consumer))

    if (!response.data?.success) {
      if (isValidationError(response.data)) {
        yield put(
          resolvePrizeRegistration({
            error: {
              message: response.data?.message,
              code: PRIZE_ERROR_CODES.VALIDATION_ERROR,
              errors: response.data?.errors
            }
          })
        )
        return
      }

      const businessError = new Error(response.data?.message || GENERIC_ERROR_MESSAGE)
      businessError.code = response.data?.code

      throw businessError
    }

    const existingConsumer = yield select((state) => state.prizeReducers.consumer)
    const voucher = response.data?.data?.[0]?.voucher

    yield put(
      resolvePrizeRegistration({
        consumer: {
          ...existingConsumer,
          consumerCpf: consumer.consumerCpf,
          voucher: {
            ...existingConsumer?.voucher,
            ...voucher
          }
        }
      })
    )

    yield put(push(`/${campaignId}/CADASTRO_CONCLUIDO`))
  } catch (error) {
    const apiError = error?.response?.data

    newrelic.noticeError(error, { response: apiError, campaignId: payload?.campaignId })

    yield put(push(`/${campaignId}/SISTEMA_INDISPONIVEL`))
  }
}

export default all([
  takeLatest('prize/IDENTIFY_CONSUMER', identifyConsumer),
  takeLatest('prize/REGISTER_CONSUMER', registerConsumer)
])
